/**
 * Distribution API endpoints for content distribution to platforms.
 */

import { api } from './client';
import type {
  ApiResponse,
  QueueDistributionRequest,
  DistributionStatusItem,
  CopyPasteResponse,
} from '../types';

const siteOptions = (siteSlug?: string) => siteSlug ? { headers: { 'x-wpp-site-slug': siteSlug } } : undefined;

/** Distribution API */
export const distributionApi = {
  /** Queue content for distribution to selected platforms */
  queue: (data: QueueDistributionRequest, siteSlug?: string) =>
    api.post<ApiResponse<{ queue_ids: string[] }>>('/v1/distribution/queue', data, siteOptions(siteSlug)),

  /** Process the distribution queue (send all pending items) */
  process: (siteSlug?: string) =>
    api.post<ApiResponse<{ processed: number; succeeded: number; failed: number; skipped: number }>>('/v1/distribution/process', {}, siteOptions(siteSlug)),

  /** Get distribution status for a content item */
  getStatus: (contentId: string, siteSlug?: string) =>
    api.get<ApiResponse<DistributionStatusItem[]>>(`/v1/distribution/status/${contentId}`, siteOptions(siteSlug)),

  /** Send a specific queue item immediately */
  sendNow: (queueId: string, siteSlug?: string) =>
    api.post<ApiResponse<{ platform_post_id?: string; platform_post_url?: string }>>(`/v1/distribution/queue/${queueId}/send`, {}, siteOptions(siteSlug)),

  /** Cancel a queued item */
  cancel: (queueId: string, siteSlug?: string) =>
    api.post<ApiResponse<null>>(`/v1/distribution/queue/${queueId}/cancel`, {}, siteOptions(siteSlug)),

  /** Retry a failed item */
  retry: (queueId: string, siteSlug?: string) =>
    api.post<ApiResponse<null>>(`/v1/distribution/queue/${queueId}/retry`, {}, siteOptions(siteSlug)),

  /**
   * Get the manual copy-paste kit for a platform (AB#298): the already-
   * transformed post text with the canonical URL, ready for the clipboard.
   */
  copyPaste: (
    platform: string,
    data: { site_slug: string; content_type: string; content_id: string; locale?: string },
  ) =>
    api.post<ApiResponse<CopyPasteResponse>>(`/v1/distribution/copy-paste/${platform}`, data),
};
